// ---------------------------------------------------------------------------
// Central SEO configuration
// ---------------------------------------------------------------------------
// Single source of truth for every per-route, per-language meta tag.
// Consumed by <SeoManager/> (src/Util/Seo.js), which writes the values into the
// document <head> on every navigation and language change.
//
// NOTE on architecture: the site serves all three languages (de/en/zh) from the
// same URLs and decides the language client-side. That makes URL-based hreflang
// alternates impossible, so we deliberately do not emit hreflang here. German is
// the static default baked into public/index.html for crawlers without JS.
// ---------------------------------------------------------------------------

export const SITE_URL = "https://www.sinis.de";
export const SITE_NAME = "SINIS GmbH";

// A wide hero image works best for link previews. webp is widely supported; if a
// platform ever fails to render it, drop a 1200x630 og-image.jpg in /public and
// point DEFAULT_OG_IMAGE at it.
export const DEFAULT_OG_IMAGE = `${SITE_URL}/images/shanghai.webp`;

export const OG_LOCALE = {
    de: "de_DE",
    en: "en_US",
    zh: "zh_CN",
};

// HTML lang attribute value per app language.
export const HTML_LANG = {
    de: "de",
    en: "en",
    zh: "zh-CN",
};

// ---------------------------------------------------------------------------
// Per-language, per-path metadata.
// Title: ~50-60 chars. Description: ~150-160 chars. Keep brand suffix on titles.
// ---------------------------------------------------------------------------
export const META = {
    de: {
        "/": {
            title: "China Consulting & Beratung für Unternehmen | SINIS GmbH",
            description:
                "SINIS GmbH begleitet Unternehmen beim erfolgreichen China-Geschäft: Markteintritt, Investition, Einkauf, Vertrieb und Fachkräfte – mit Expertise seit 2002.",
            keywords:
                "China Consulting, China Beratung, Markteintritt China, Einkauf China, Vertrieb China, Fachkräfte China, Unternehmensberatung China, SINIS GmbH",
        },
        "/invest": {
            title: "In China investieren – Markteintritt & Wachstum | SINIS GmbH",
            description:
                "Strategische Investitionsberatung für China: Markteintritt, Standortwahl und Wachstum strategisch sichern. SINIS GmbH unterstützt Sie mit Expertise vor Ort.",
            keywords: "In China investieren, Markteintritt China, Investitionsberatung China, Standortwahl China",
        },
        "/purchase": {
            title: "Einkauf & Beschaffung in China | SINIS GmbH",
            description:
                "Effiziente Beschaffung in China: Lieferantensuche, Qualitätssicherung und Kostenoptimierung. SINIS GmbH optimiert Ihren Einkauf mit starkem lokalem Netzwerk.",
            keywords: "Einkauf China, Beschaffung China, Lieferantensuche China, Qualitätssicherung China",
        },
        "/distribution": {
            title: "Vertrieb in China & Europa | SINIS GmbH",
            description:
                "Vertrieb strategisch ausbauen: SINIS GmbH bringt Ihre Produkte in den chinesischen und europäischen Markt – mit Qualität, Kostenvorteilen und lokalem Netzwerk.",
            keywords: "Vertrieb China, Distribution China, Produkte verkaufen China, Markterschließung China",
        },
        "/recruitment": {
            title: "Fachkräfte aus China gewinnen – Recruitment | SINIS GmbH",
            description:
                "Internationale Fachkräfte für Ihr Unternehmen: SINIS GmbH vermittelt qualifiziertes Personal aus China – nachhaltig, zuverlässig und passgenau für Ihren Bedarf.",
            keywords: "Fachkräfte China, Recruitment China, Personalvermittlung China, internationale Fachkräfte",
        },
        "/about": {
            title: "Über uns – China-Beratung seit 2002 | SINIS GmbH",
            description:
                "Seit 2002 steht SINIS GmbH südlich von München für professionelle China-Beratung. Unser Team in Deutschland und China begleitet Sie bei Markteintritt und Projekten.",
            keywords: "Über SINIS, China Beratung München, SINIS GmbH, China-Geschäft Beratung",
        },
        "/contact": {
            title: "Kontakt – China Consulting | SINIS GmbH",
            description:
                "Nehmen Sie Kontakt mit SINIS GmbH auf. Wir beraten Sie persönlich zu Investition, Einkauf, Vertrieb und Fachkräften im China-Geschäft.",
            keywords: "Kontakt SINIS, China Beratung Kontakt, China Consulting München",
        },
        "/imprint": {
            title: "Impressum | SINIS GmbH",
            description: "Impressum der SINIS GmbH, Waldstr. 14, 85635 Höhenkirchen, Deutschland.",
        },
        "/privacy-policy": {
            title: "Datenschutzerklärung | SINIS GmbH",
            description:
                "Datenschutzerklärung der SINIS GmbH – Informationen zur Verarbeitung personenbezogener Daten gemäß DSGVO.",
        },
    },

    en: {
        "/": {
            title: "China Consulting & Business Advisory | SINIS GmbH",
            description:
                "SINIS GmbH helps companies succeed in China: market entry, investment, purchasing, distribution and skilled professionals – with expertise since 2002.",
            keywords:
                "China consulting, China business advisory, market entry China, sourcing China, distribution China, skilled workers China, SINIS GmbH",
        },
        "/invest": {
            title: "Investing in China – Market Entry & Growth | SINIS GmbH",
            description:
                "Strategic investment advisory for China: market entry, location strategy and securing sustainable growth. SINIS GmbH supports you on the ground.",
            keywords: "investing in China, China market entry, China investment advisory, location strategy China",
        },
        "/purchase": {
            title: "Purchasing & Sourcing in China | SINIS GmbH",
            description:
                "Efficient sourcing in China: supplier search, quality assurance and cost optimisation. SINIS GmbH streamlines your procurement with a strong local network.",
            keywords: "purchasing China, sourcing China, supplier search China, quality assurance China",
        },
        "/distribution": {
            title: "Distribution in China & Europe | SINIS GmbH",
            description:
                "Expand your distribution strategically: SINIS GmbH brings your products to the Chinese and European markets – quality, cost advantages and a strong network.",
            keywords: "distribution China, selling in China, market access China, distribution Europe",
        },
        "/recruitment": {
            title: "Recruiting Skilled Professionals from China | SINIS GmbH",
            description:
                "International skilled workers for your company: SINIS GmbH recruits qualified professionals from China – sustainable, reliable and tailored to your needs.",
            keywords: "skilled workers China, recruitment China, hiring from China, international professionals",
        },
        "/about": {
            title: "About Us – China Consulting since 2002 | SINIS GmbH",
            description:
                "Since 2002, SINIS GmbH south of Munich has stood for professional China consulting. Our team in Germany and China supports your market entry and projects.",
            keywords: "about SINIS, China consulting Munich, SINIS GmbH, China business consulting",
        },
        "/contact": {
            title: "Contact – China Consulting | SINIS GmbH",
            description:
                "Get in touch with SINIS GmbH. We advise you personally on investment, purchasing, distribution and skilled professionals in the China business.",
            keywords: "contact SINIS, China consulting contact, China business Munich",
        },
        "/imprint": {
            title: "Imprint | SINIS GmbH",
            description: "Imprint of SINIS GmbH, Waldstr. 14, 85635 Höhenkirchen, Germany.",
        },
        "/privacy-policy": {
            title: "Privacy Policy | SINIS GmbH",
            description:
                "Privacy policy of SINIS GmbH – information on the processing of personal data in accordance with the GDPR.",
        },
    },

    zh: {
        "/": {
            title: "醒狮中德商联 SINIS GmbH | 中德经贸与投资咨询",
            description:
                "醒狮中德商联（SINIS GmbH）成立于2002年，总部位于德国慕尼黑南部，提供投资咨询、签证移民、公司设立、市场拓展等一站式中德经贸合作服务。",
            keywords: "中德经贸, 德国投资咨询, 德国签证移民, 德国公司设立, 中欧贸易, 醒狮中德商联, SINIS",
        },
        "/visa-consulting": {
            title: "德国签证与移民咨询 | 醒狮中德商联 SINIS",
            description:
                "提供德国留学、工作、商务及家庭团聚等签证与移民咨询服务，助您顺利赴德。醒狮中德商联（SINIS GmbH）专业团队全程支持。",
            keywords: "德国签证, 德国移民, 德国留学签证, 德国工作签证, 德国商务签证",
        },
        "/business": {
            title: "德国商务与投资咨询 | 醒狮中德商联 SINIS",
            description:
                "协助在德设立公司、子公司或代表处，提供合规注册、市场拓展与投资咨询服务。醒狮中德商联助力中国企业拓展欧洲市场。",
            keywords: "德国公司注册, 德国投资, 德国子公司, 德国市场拓展, 德国商务咨询",
        },
        "/international-trade": {
            title: "中欧国际贸易服务 | 醒狮中德商联 SINIS",
            description:
                "提供全面的进出口贸易服务，包括商品进出口、销售代理与市场调研，搭建高效专业的中欧双向经贸合作平台。",
            keywords: "中欧贸易, 进出口贸易, 销售代理, 德国进出口, 国际贸易咨询",
        },
        "/contact": {
            title: "联系我们 | 醒狮中德商联 SINIS GmbH",
            description: "联系醒狮中德商联（SINIS GmbH），获取签证移民、商务投资与国际贸易方面的专业咨询与支持。",
            keywords: "联系醒狮, SINIS 联系方式, 德国咨询联系",
        },
        "/imprint": {
            title: "公司信息 | 醒狮中德商联 SINIS GmbH",
            description: "醒狮中德商联（SINIS GmbH）公司信息，Waldstr. 14, 85635 Höhenkirchen, 德国。",
        },
        "/privacy-policy": {
            title: "隐私政策 | 醒狮中德商联 SINIS GmbH",
            description: "醒狮中德商联（SINIS GmbH）隐私政策 – 依据欧盟《通用数据保护条例》（GDPR）处理个人数据的相关说明。",
        },
    },
};

// Brand-only fallback used for unknown routes (e.g. NotFound). These are marked
// noindex by the SeoManager so empty/404 pages never enter the index.
export const FALLBACK = {
    de: {
        title: "SINIS GmbH – China Consulting & Beratung",
        description:
            "SINIS GmbH – professionelle Beratung für Ihr China-Geschäft: Investition, Einkauf, Vertrieb und Fachkräfte.",
    },
    en: {
        title: "SINIS GmbH – China Consulting & Business Advisory",
        description:
            "SINIS GmbH – professional advisory for your China business: investment, purchasing, distribution and skilled professionals.",
    },
    zh: {
        title: "醒狮中德商联 SINIS GmbH",
        description: "醒狮中德商联（SINIS GmbH）– 专业的中德经贸与投资咨询服务。",
    },
};

/**
 * Resolve metadata for a given language + pathname.
 * Returns { title, description, keywords, isKnown } where isKnown=false marks
 * routes that should be excluded from indexing.
 */
export function resolveMeta(language, pathname) {
    const lang = META[language] ? language : "de";
    const table = META[lang];
    const normalized = pathname !== "/" && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;

    if (table[normalized]) {
        return { ...table[normalized], isKnown: true };
    }
    return { ...FALLBACK[lang], keywords: undefined, isKnown: false };
}
